/**
 * The colours every multi-series visualization draws from: chart series, pie slices, treemap
 * tiles, a map's marker groups. One list, so the second series is the same violet in a bar chart
 * as it is on the map beside it, and so changing the palette is one edit rather than a hunt.
 *
 * Colours are named and then ordered. The names exist so a colour can be referred to as
 * "violet" rather than as a hex string a caller has to keep in sync; the order is the priority
 * every renderer follows, so series one is always cyan and series two is always violet.
 *
 * This literal palette plays two different roles depending on the renderer. A DOM chart never
 * reads it directly: its default comes from the `--cirra-chart-series-N` CSS token baked into
 * the stylesheet, and this file's values are only the ones that token was seeded with. A
 * renderer that cannot read a CSS variable (Mapbox marker paint, the choropleth fill ramp) reads
 * this palette as its literal fallback when a series-color slot is unset. Either way, a theme's
 * resolved slot (`resolveAnalyticsPageStyleVars`, `resolveAnalyticsSeriesPalette`) overrides both
 * paths the same way, so a themed page never shows this file's colours next to its own.
 */

/* Literal hex, not CSS variables: a map style expression is evaluated outside the DOM, so it
   cannot resolve `var(--cirra-...)`. That constraint is why this palette was already literal.
   These are not the Cirra brand tokens (`--cirra-info`, `--cirra-success`, `--cirra-danger`),
   which are tuned for status chrome rather than for sitting next to each other in a plot. */
export const ANALYTICS_SERIES_COLORS = {
  cyan: '#00a4e6',
  violet: '#7554c9',
  amber: '#d76034',
  teal: '#19886f',
  rose: '#b44f85',
  olive: '#7e8134',
  indigo: '#4665b7',
  coral: '#b95048'
};

/* The order colours are handed out in. First series cyan, second violet, and so on; past the end
   it wraps. Reordering this changes every chart at once, which is the point. */
export const ANALYTICS_SERIES_COLOR_ORDER = ['cyan', 'violet', 'amber', 'teal', 'rose', 'olive', 'indigo', 'coral'];

export const ANALYTICS_SERIES_PALETTE = ANALYTICS_SERIES_COLOR_ORDER.map((name) => ANALYTICS_SERIES_COLORS[name]);

/* Stable colour for a value's position in a series or group list: same index, same colour,
   every render. Read the palette through this rather than indexing it, so the wrap rule lives
   in one place. */
export const toAnalyticsSeriesColor = (index = 0) => ANALYTICS_SERIES_PALETTE[Math.abs(Math.floor(Number(index) || 0)) % ANALYTICS_SERIES_PALETTE.length];

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/* A hex colour any consumer (map style expressions, theme colour slots) can trust. The one
   definition of "valid hex" for Analytics, so a colour accepted here is never later rejected by a
   sibling regex that quietly drifted from it. */
export const isAnalyticsHexColor = (value) => HEX_COLOR.test(String(value || ''));

/* The red, green and blue bytes of any hex colour this file accepts. Shorthand digits are
   doubled and any alpha is dropped, so '#abc' and '#aabbccff' both read as [170, 187, 204].
   Every consumer that has to do arithmetic on a colour goes through this, so a colour a user
   typed short cannot mean one thing to a contrast check and another to a map paint ramp. */
export const toAnalyticsRgbChannels = (hex) => {
  const raw = String(hex || '').replace('#', '');
  const isShort = raw.length === 3 || raw.length === 4;
  const pairs = isShort
    ? raw
        .slice(0, 3)
        .split('')
        .map((digit) => digit + digit)
    : [raw.slice(0, 2), raw.slice(2, 4), raw.slice(4, 6)];

  return pairs.map((pair) => parseInt(pair, 16));
};

/* The six digit form of a hex colour. Renderers that parse a literal rather than read the DOM
   (Mapbox style expressions, a native colour input) only accept this form, and comparing two
   colours for equality only works once both are written the same way. */
export const toAnalyticsSixDigitHex = (hex) =>
  `#${toAnalyticsRgbChannels(hex)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')}`;

/* Black or white ink for a fill color, by WCAG relative luminance on linearized sRGB (threshold
   0.179): light fills read best with black text, dark fills with white. Used wherever a label is
   painted directly over a series color rather than beside it: a treemap tile, a color swatch's
   slot number. */
export const toAnalyticsContrastInk = (hex) => {
  const [r, g, b] = toAnalyticsRgbChannels(hex).map((byte) => {
    const channel = byte / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance > 0.179 ? '#000000' : '#ffffff';
};

/* A hex colour a style expression can be trusted with. Anything else falls back, because an
   invalid colour in a Mapbox paint property throws rather than degrading. */
export const toSafeHexColor = (value, fallback = ANALYTICS_SERIES_PALETTE[0]) => (isAnalyticsHexColor(value) ? String(value) : fallback);
